// v8.9.4 — Market condition indicator badge.
//
// Reads /api/bot/market-condition every 12s and surfaces a single
// pill that answers "why is the bot quiet?":
//   🟢 TRENDING — clear directional pressure, signals likely
//   🟡 RANGING  — mild trend, fewer signals expected
//   🔴 CHOP     — sideways markets, signals rare (Friday PM)
//   🔵 WARMUP   — EMA(100) still warming up
//
// Reduces support questions during Friday-PM chop / pre-news quiet
// windows by telling the user directly that low signal volume is
// expected market behaviour, not a broken bot.
import { useEffect, useState } from "react";
import { Activity, TrendingUp, ZapOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const CONDITION_META = {
  TRENDING: {
    Icon: TrendingUp,
    dot: "bg-vts-profit",
    border: "border-vts-profit/40",
    bg: "bg-vts-profit/10",
    tone: "text-vts-profit",
    emoji: "🟢",
  },
  RANGING: {
    Icon: Activity,
    dot: "bg-amber-400",
    border: "border-amber-400/40",
    bg: "bg-amber-400/10",
    tone: "text-amber-300",
    emoji: "🟡",
  },
  CHOP: {
    Icon: ZapOff,
    dot: "bg-vts-loss",
    border: "border-vts-loss/40",
    bg: "bg-vts-loss/10",
    tone: "text-vts-loss",
    emoji: "🔴",
  },
  WARMUP: {
    Icon: Loader2,
    dot: "bg-vts-blue",
    border: "border-vts-blue/40",
    bg: "bg-vts-blue/10",
    tone: "text-vts-blue",
    emoji: "🔵",
  },
};

export function MarketConditionBadge({ accountId, running }) {
  const [mc, setMc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accountId || !running) {
      setMc(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchMc = async () => {
      try {
        const { data } = await api.get(`/bot/market-condition/${accountId}`);
        if (cancelled) return;
        if (data?.ok) setMc(data);
        else setMc(null);
      } catch {
        if (!cancelled) setMc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMc();
    const id = setInterval(fetchMc, 12000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [accountId, running]);

  if (!running) return null;
  if (loading && !mc) {
    return (
      <div
        data-testid="market-condition-loading"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-vts-line bg-vts-surface/40 text-[10px] font-mono text-vts-silver/55"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Reading market…
      </div>
    );
  }
  if (!mc) return null;

  const meta = CONDITION_META[mc.condition] || CONDITION_META.WARMUP;
  const Icon = meta.Icon;
  const animate = mc.condition === "WARMUP" ? "animate-spin" : "animate-pulse";
  const gapText =
    mc.avg_gap_pct != null
      ? `avg gap ${Number(mc.avg_gap_pct).toFixed(3)}%`
      : "no readings yet";

  return (
    <div
      data-testid="market-condition-badge"
      className={`inline-flex flex-col gap-1.5 px-3 py-2 rounded-md border ${meta.border} ${meta.bg}`}
      title={mc.summary || ""}
    >
      <div className="flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} ${animate}`} />
        <Icon className={`h-3.5 w-3.5 ${meta.tone}`} />
        <span className={`text-[11px] font-mono uppercase tracking-wider font-semibold ${meta.tone}`}>
          {mc.label}
        </span>
        <span className="text-[10px] font-mono text-vts-silver/55 tabular-nums">
          · {gapText}
        </span>
      </div>
      <div className="text-[10px] font-mono text-vts-silver/70 leading-snug max-w-md">
        {mc.summary}
      </div>
      {mc.sample_size > 0 && mc.bias_counts && (
        <div className="flex items-center gap-2 text-[9px] font-mono text-vts-silver/45 uppercase tracking-wider">
          <span>{mc.sample_size} pairs</span>
          {mc.trending_count > 0 && (
            <>
              <span className="text-vts-silver/30">·</span>
              <span className="text-vts-profit/80">{mc.trending_count} trending</span>
            </>
          )}
          {(mc.bias_counts.RISE > 0 || mc.bias_counts.FALL > 0) && (
            <>
              <span className="text-vts-silver/30">·</span>
              <span className="text-vts-profit/70">{mc.bias_counts.RISE} ↑</span>
              <span className="text-vts-loss/70">{mc.bias_counts.FALL} ↓</span>
              <span className="text-vts-silver/40">{mc.bias_counts.FLAT} →</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MarketConditionBadge;
